import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoggedLayout from 'layouts/LoggedLayout';
import { CristalLoader } from 'helpers/generic';
import FieldSmallComment from 'components/FieldSmallComment';
import CustomInput from 'components/CustomInput';
import CustomSelect from 'components/CustomSelect';

let axiosCancelToken = null;

export default function TaxesForm(props) {
	const navigate = useNavigate();
	const params = useParams();

	let [data, setData] = useState({
		default: false,
		deletable: true
	});
	let [errors, setErrors] = useState({});
	let [loading, setLoading] = useState(false);

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();
	
		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		if ( params.id ) {
			const getData = async () => {
				setLoading(true);
				setData([]);

				await axios.get('/api/manager/taxes/get/' + params.id, {
					cancelToken: axiosCancelToken.token
				}).then((response) => {
			    	setData(response.data);
			  	}).catch((error) => {
					if ( axios.isCancel(error) ) return;
				});

				setLoading(false);
			}
			getData();
		} else {
			const getLastOrder = () => {
				axios.get('/api/manager/taxes/get-last-order', {
					cancelToken: axiosCancelToken.token
				}).then((response) => {
			    	setData(data => ({...data, order: response.data.order}));
			  	}).catch((error) => {
					if ( axios.isCancel(error) ) return;
				});
			}
			getLastOrder();
		}
	}, [params.id]);

	const deleteTax = () => {
		const c = window.confirm('¿Quieres eliminar este impuesto?');
		if ( !c ) return;

		axios.delete('/api/manager/taxes/delete/' + data.id, {}, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
	    	toast.info('Impuesto borrado')
	    	navigate('/taxes');
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});	
	}

	const saveData = (goBack = true) => {
		setErrors({});

		axios.post('/api/manager/taxes/save' + (data.id ? '/' + data.id : ''), data, {
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			toast.success('Datos guardados');
			setData({...response.data.tax});
			if ( goBack ) navigate('/taxes');
			else {
		    	navigate('/taxes/edit/' + response.data.tax.id);
			}
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
			if ( error.response.data.errors ) setErrors(error.response.data.errors);
			toast.error('Ha ocurrido un error al guardar');
		});	
	}

	const setDataField = (field, value) => {
		setData({...data, [field]: value});
	}

	return (
		<LoggedLayout>
			{ loading && <CristalLoader /> }

			<section>
				<div className="page-title">
					<h1>Ficha de impuesto</h1>
					<button onClick={() => navigate('/taxes')} className="btn btn-sm btn-light ms-auto">Volver</button>
				</div>

				<div className="page-content">
					<div className="row justify-content-center">
						<div className="col-lg-12">
							<div className="card border-0 shadow-sm">
								<div className="card-body">
									<div className="row">
										<div className="row">
											<div className="col-lg-3">
												<div className="mb-2">
													<CustomInput label="Nombre" type="text" className="form-control form-control-sm" onChange={(e) => setDataField('name', e.target.value)} value={data.name ?? ''} />
													{ errors.name &&
														<div className="invalid-feedback d-block">{ errors.name[0] }</div>
													}
												</div>
											</div>
											<div className="col-lg-3">
												<div className="mb-2">
													<CustomInput label="Porcentaje" type="number" step="0.01" className="form-control form-control-sm" onChange={(e) => setDataField('percent', e.target.value)} value={data.percent ?? ''} readOnly={data.deletable === true ? false : true} />
													{ data.deletable === false &&
														<FieldSmallComment className="text-secondary d-flex h-100 align-items-center">El porcentaje no se puede modificar por que ya está siendo utilizado en facturas</FieldSmallComment>
													}
													{ errors.percent &&
														<div className="invalid-feedback d-block">{ errors.percent[0] }</div>
													}
												</div>
											</div>
											<div className="col-lg-3">
												<div className="mb-2">
													<CustomInput label="Orden" type="number" className="form-control form-control-sm" onChange={(e) => setDataField('order', e.target.value)} value={data.order ?? ''} />
													<FieldSmallComment className="mt-1">Este campo determina el orden en el que apareceran los impuestos en los formularios</FieldSmallComment>
													{ errors.order &&
														<div className="invalid-feedback d-block">{ errors.order[0] }</div>
													}
												</div>
											</div>
											<div className="col-lg-3">
												<div className="mb-2">
													<CustomSelect label="Por defecto" className="form-control form-control-sm" onChange={(e) => setDataField('default', e.target.value)} value={data.default ?? ''}>
														<option value="0">No</option>
														<option value="1">Si</option>
													</CustomSelect>
													<FieldSmallComment className="mt-1">Si se marca por defecto aparecerá como impuesto preseleccionado en los formularios donde se tenga que seleccionar un impuesto</FieldSmallComment>
													{ errors.default &&
														<div className="invalid-feedback d-block">{ errors.default[0] }</div>
													}
												</div>
											</div>
										</div>	
									</div>
								</div>
								<div className="card-footer">
									<div className="row">
										<div className="col-4">
											{ data.id &&
												<React.Fragment>
													{ data.deletable === true ?
														<button className="btn btn-sm btn-link text-danger" tabIndex="-1" onClick={() => deleteTax()}>Eliminar</button>							
														:
														<FieldSmallComment className="text-danger d-flex h-100 align-items-center">Este impuesto no se puede eliminar por que está siendo utilizado en facturas</FieldSmallComment>
													}
												</React.Fragment>
											}
										</div>
										<div className="col-8 text-end">
											<button className="btn btn-sm btn-primary text-white d-inline ms-3" onClick={() => saveData(false)}>Guardar</button>							
											<button className="btn btn-sm btn-primary text-white d-inline ms-3" onClick={() => saveData()}>Guardar y salir</button>							
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</LoggedLayout>
	);
}